// TelcoTrack — PPD import (Tabulka_PPD.xlsx) a Ceník Protelco
// Zdroj šablon: docs/infrastructure/export_tables/Tabulka_PPD.xlsx, Ceník_Protelco.xlsx

import type { FinanceComparisonBucketRow } from './ppd-finance-categories.constants';

export type PpdLineKind = 'work' | 'material' | 'jv';
/** `procurement` = nejlevnější platná cena ze Správy ceníku; `components` legacy alias. */
export type PpdPriceSource =
  | 'protelco'
  | 'procurement'
  | 'components'
  | 'jv'
  | 'ppd'
  | 'missing';

/** Jedna položka z parsovaného PPD souboru (před oceněním). */
export interface PpdParsedLine {
  sortOrder: number;
  category: string | null;
  sap: string;
  isConstruction: boolean;
  name: string;
  quantity: number;
  unit: string;
  /** Celková cena z exportu PPD (sloupec „Celková cena“), pokud je k dispozici. */
  ppdLineTotal?: number | null;
}

/** Metadata z hlavičky PPD (název stavby, SPP, …). */
export interface PpdParsedMetadata {
  buildName?: string;
  sppNumber?: string;
  catalogLabel?: string;
  catalogVersion?: string;
  /** Ostatní páry label → hodnota z hlavičky. */
  extra: Record<string, string>;
}

export interface PpdParseResult {
  metadata: PpdParsedMetadata;
  lines: PpdParsedLine[];
}

/** Položka z Ceníku Protelco (parsovaná z XLSX). */
export interface ProtelcoCatalogItem {
  sap: string;
  name: string;
  unit: string;
  unitPrice: number;
  sheetName: string;
}

export interface ProtelcoCatalogAnalyzeResult {
  total: number;
  bySheet: Record<string, number>;
  duplicateSaps: string[];
}

export interface ProtelcoCatalogImportResult {
  imported: number;
  sourceFileName?: string;
}

export interface PpdPricedLine extends PpdParsedLine {
  unitPrice: number | null;
  lineTotal: number;
  lineKind: PpdLineKind;
  priceSource: PpdPriceSource;
}

export interface PpdAnalyzeResult {
  total: number;
  workLines: number;
  materialLines: number;
  jvLines: number;
  missingPriceLines: number;
  plannedWorkRevenue: number;
  plannedMaterialCost: number;
  catalogLoaded: boolean;
  catalogItemCount: number;
  /** Rozpad plánovaných nákladů dle kategorií PPD (práce / materiál). */
  categoryBreakdown?: FinanceComparisonBucketRow[];
  /** Upozornění (např. neúplný export .xls doplněný z rekapitulace). */
  warnings?: string[];
  /** Diagnostika parsování/ocenění (dev / DEBUG_PPD=1). */
  diagnostics?: Record<string, unknown>;
  preview: Array<{
    sap: string;
    name: string;
    quantity: number;
    unit: string;
    lineKind: PpdLineKind;
    lineTotal: number;
    priceSource: PpdPriceSource;
  }>;
}

export interface PpdCommitResult {
  total: number;
  plannedWorkRevenue: number;
  plannedMaterialCost: number;
  unpricedLineCount: number;
}

export interface PpdImportSummary {
  polygonId: string;
  sourceFileName: string | null;
  buildName: string | null;
  sppNumber: string | null;
  plannedWorkRevenue: number;
  plannedMaterialCost: number;
  unpricedLineCount: number;
  importedAt: string;
  lineCount: number;
}

export interface FinanceComparisonResult {
  hasPpd: boolean;
  plannedWorkRevenue: number;
  plannedMaterialCost: number;
  plannedTotal: number;
  actualWorkCost: number;
  actualMaterialCost: number;
  actualTotal: number;
  workMargin: number;
  materialVariance: number;
  totalVariance: number;
  /** Rozpad po kategoriích PPD (práce: stavba/montáž/…, materiál dle sekcí PPD). */
  buckets?: FinanceComparisonBucketRow[];
}

/** Srovnání plánovaného materiálu (PPD) vs skutečně použitého — agregace po SAP. */
export interface FinanceMaterialComparisonLine {
  sap: string;
  name: string;
  unit: string | null;
  plannedQuantity: number;
  plannedCost: number;
  actualQuantity: number;
  actualCost: number;
  /** plannedCost − actualCost (kladné = pod plánem). */
  costVariance: number;
  /** PPD finance bucket (`material_ostatni`, …) — jen u material-lines. */
  financeBucket?: string;
  /** Strom katalogu (`components`) — stejné jako Materiály / Souhrn. */
  mainCategory?: string | null;
  subCategory?: string | null;
  branch1?: string | null;
  branch2?: string | null;
  branch3?: string | null;
  branch4?: string | null;
}

export interface FinanceMaterialComparisonResult {
  hasPpd: boolean;
  lines: FinanceMaterialComparisonLine[];
  plannedCostTotal: number;
  actualCostTotal: number;
  costVarianceTotal: number;
}

/** Srovnání plánované práce (PPD × Protelco) vs skutečného nákladu výkazu — agregace po CETIN SAP. */
export type FinanceWorkComparisonLine = FinanceMaterialComparisonLine;

export interface FinanceWorkComparisonResult {
  hasPpd: boolean;
  lines: FinanceWorkComparisonLine[];
  /** Součet plánovaného příjmu (PPD). */
  plannedCostTotal: number;
  /** Součet skutečných nákladů výkazu (ceny pro zaměstnance). */
  actualCostTotal: number;
  /** plannedCostTotal − actualCostTotal (marže). */
  costVarianceTotal: number;
  /** Upozornění — např. PPD importováno jen z rekapitulace (.xls), chybí plán po SAP. */
  detailWarning?: string | null;
}
